import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaChartLine, FaExclamationTriangle, FaFirefox, FaChartArea } from 'react-icons/fa';
import { createTrade, runBacktest } from '../services/api';
import Button from './ui/Button';
import BacktestResults from './BacktestResults';

const FormContainer = styled.div`
  margin-top: ${props => props.theme.spacing.lg};
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSize.lg};
  margin-bottom: ${props => props.theme.spacing.md};
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -5px;
    width: 40px;
    height: 2px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
  }
`;

const CoinInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background.card};
  border-radius: ${props => props.theme.borderRadius.md};
  border-left: 4px solid ${props => props.theme.colors.accent};
`;

const CoinName = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
`;

const CoinPrice = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.fontSize.lg};
`;

const TokenAmountLabel = styled.div`
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.theme.colors.text.muted};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
`;

const InputWrapper = styled.div`
  position: relative;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  padding-right: ${props => props.$hasIcon ? props.theme.spacing.xxl : props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background.card};
  color: ${props => props.theme.colors.text.primary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.md};
  transition: all ${props => props.theme.transitions.fast};
  outline: none;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primaryAlpha(0.2)};
  }

  &::placeholder {
    color: ${props => props.theme.colors.text.muted};
  }

  ${props => props.$hasIcon && `
padding-left: 16px;
  `}
`;

const InputSuffix = styled.div`
  position: absolute;
  right: ${props => props.theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.text.muted};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  pointer-events: none;
`;


const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.status.error};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background-color: rgba(231, 76, 60, 0.1);
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  border-left: 3px solid ${props => props.theme.colors.status.error};
`;

const DEFAULT_TAKE_PROFIT_PERCENT = 1.2;

const MetamaskButton = styled(Button)`
  background: #F6851B;
  color: #fff;
  margin-top: ${props => props.theme.spacing.md};

  &:hover:not(:disabled) {
    background: #E2761B;
  }
`;

const BacktestButton = styled(Button)`
  background: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.lg};
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
  }
`;

const InfoTooltip = styled.div`
  margin-top: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm};
  background-color: rgba(40, 160, 240, 0.1);
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const TimeframeOptions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const TimeframeOption = styled.div`
  flex: 1;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  background-color: ${props => 
    props.selected 
      ? props.theme.colors.primary
      : props.theme.colors.background.card
  };
  border: 2px solid ${props => 
    props.selected 
      ? props.theme.colors.primary
      : props.theme.colors.border
  };
  color: ${props => 
    props.selected 
      ? '#FFFFFF'
      : props.theme.colors.text.primary
  };
  text-align: center;
  font-weight: 600;
  font-size: ${props => props.theme.fontSize.sm};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }
`;

// Utility function for comma to dot conversion
const normalizeNumberInput = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(',', '.');
};

const TradeForm = ({ wallet, exchange, coin, currentPrice, onPositionCreated, onConnectWallet }) => {
  const [amount, setAmount] = useState('10');
  const [takeProfitPercent, setTakeProfitPercent] = useState('1.5');
  const [takeProfitPrice, setTakeProfitPrice] = useState('');
  const [timeframe, setTimeframe] = useState('5m');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Simplified: always use long side and USDT
  const side = 'long';

  
  // Backtest state
  const [backtestResults, setBacktestResults] = useState(null);
  const [backtestRun, setBacktestRun] = useState(false);
  const [backtestLoading, setBacktestLoading] = useState(false);

  // Simplified: always use USDT
  const currentCurrency = 'USDT';
  
  // Calculate token amount based on USDT amount
  const calculateTokenAmount = () => {
    if (!amount || !coin.price || parseFloat(amount) <= 0 || coin.price <= 0) {
      return '0.00000000';
    }
    const tokenAmount = parseFloat(amount) / coin.price;
    return tokenAmount.toFixed(8);
  };

  // Run backtest function - memoized to prevent unnecessary re-renders
  const runBacktestAnalysis = useCallback(async () => {
    if (!coin || !coin.address) {
      setError('Please select a coin first');
      return;
    }
    
    if (!amount || !takeProfitPercent) {
      setError('Amount and Take Profit are required');
      return;
    }
    
    setBacktestLoading(true);
    setError(null);
    
    try {
      const results = await runBacktest(
        exchange,
        coin.address,
        side,
        amount,
        parseFloat(normalizeNumberInput(takeProfitPercent)) / 100,  // Send percentage as decimal (1.2% = 0.012)
        timeframe
      );
      
      if (results.error) {
        throw new Error(results.error);
      }
      
      setBacktestResults(results);
      setBacktestRun(true); // Mark that backtest has been run - this enables auto-updates
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
      });
    } catch (err) {
      console.error('Error running backtest:', err);
      setError(err.message || 'Failed to run backtest');
    } finally {
      setBacktestLoading(false);
    }
  }, [coin, amount, takeProfitPercent, side, exchange, timeframe]);

  // Update take profit input handler to normalize comma to dot
  const handleTakeProfitChange = (value) => {
    const normalizedValue = normalizeNumberInput(value);
    setTakeProfitPercent(normalizedValue);
  };

  // Calculate take profit price when percent changes or coin changes
  useEffect(() => {
    if (coin && coin.price && takeProfitPercent) {
      const percentValue = parseFloat(normalizeNumberInput(takeProfitPercent)) / 100;
      let targetPrice;

      if (side === 'long') {
        // For long positions, increase price by percentage
        targetPrice = coin.price * (1 + percentValue);
      } else {
        // For short positions, decrease price by percentage
        targetPrice = coin.price * (1 - percentValue);
      }

      setTakeProfitPrice(targetPrice.toFixed(8));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coin, takeProfitPercent, side]);
  
  // Auto-run backtest when inputs change (after initial backtest has been run)
  useEffect(() => {
    if (!backtestRun) return; // Don't auto-run before first manual run
    if (!coin || !coin.address || !amount || !takeProfitPercent) return;
    
    let cancelled = false;
    setBacktestLoading(true);
    
    const run = async () => {
      try {
        const results = await runBacktest(
          exchange,
          coin.address,
          side,
          amount,
          parseFloat(normalizeNumberInput(takeProfitPercent)) / 100,  // Send percentage as decimal
          timeframe
        );
        
        if (!cancelled) {
          if (results.error) {
            throw new Error(results.error);
          }
          setBacktestResults(results);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error running auto-backtest:', err);
          setError(err.message || 'Failed to run backtest');
        }
      } finally {
        if (!cancelled) {
          setBacktestLoading(false);
        }
      }
    };
    
    run();
    return () => { cancelled = true; };
  }, [amount, takeProfitPercent, coin, side, exchange, backtestRun, timeframe]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If wallet is not connected, connect MetaMask directly
    if (!wallet) {
      onConnectWallet && onConnectWallet();
      return;
    }

    if (!amount || !takeProfitPercent) {
      setError('Amount and Take Profit are required');
      return;
    }

    // Validate take profit
    const percentValue = parseFloat(normalizeNumberInput(takeProfitPercent));
    if (isNaN(percentValue) || percentValue <= 0) {
      setError('Take Profit percentage must be greater than 0');
      return;
    }

    // Validate compatibility between wallet network and DEX
    if (wallet.network_id) {
      // Network validation for Ethereum-based wallets
      if (['1', '5', '11155111'].includes(wallet.network_id) && exchange !== 'pancakeswap') {
        setError(`Ethereum wallet cannot trade on ${exchange}. Please switch to PancakeSwap.`);
        return;
      }

      // Network validation for BSC-based wallets
      if (['56', '97'].includes(wallet.network_id) && exchange !== 'pancakeswap') {
        setError(`BSC wallet cannot trade on ${exchange}. Please switch to PancakeSwap.`);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Include network_id from wallet if available
      const result = await createTrade(
        wallet.address,
        exchange,
        coin.address,
        coin.symbol,
        side,
        amount,
        takeProfitPercent,
        timeframe
      );

      // Check if the result contains an error
      if (result && result.error) {
        // Handle error returned from the API service
        throw new Error(result.error);
      }

      // Handle immediate execution case (sufficient balance)
      if (result && result.success && result.position) {
        // Show success message for immediate execution
        setError(null);
        
        // Notify parent component with the opened position
        onPositionCreated && onPositionCreated(result.position);
        
        // Clear form values
        setAmount('10');
        setTakeProfitPercent(DEFAULT_TAKE_PROFIT_PERCENT.toString());
        
        // Show success message
        setError(`✅ ${result.message || 'Trade executed successfully!'}`);
        
        return;
      }

      // Handle pending confirmation case
      if (result && result.pending) {
        // Show success message for pending confirmation
        setError(null);
        
        // Create a position with pending status for immediate UI feedback
        const pendingPosition = {
          ...result.position,
          status: 'confirming_funding',
          confirmations: result.confirmations || 0,
          required_confirmations: result.required_confirmations || 30
        };
        
        // Notify parent component
        onPositionCreated && onPositionCreated(pendingPosition);
        
        // Clear form values
        setAmount('10');
        setTakeProfitPercent(DEFAULT_TAKE_PROFIT_PERCENT.toString());
        
        // Show informational message
        setError(`✅ Transaction submitted! Waiting for confirmations (${result.confirmations || 0}/${result.required_confirmations || 30}). Your trade will execute automatically once confirmed.`);
        
        return;
      }

      // If no error and not pending, proceed with success (fallback for other cases)
      onPositionCreated && onPositionCreated(result);

      // Only clear form values on success
      setAmount('10');
      setTakeProfitPercent(DEFAULT_TAKE_PROFIT_PERCENT.toString());
    } catch (err) {
      console.error('Error creating trade:', err);

      // Extract error message from various possible formats
      let errorMessage = 'Failed to create trade';

      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (typeof err.message === 'string') {
        errorMessage = err.message;
      }

      // Check if error message contains "User denied transaction signature"
      if (errorMessage.includes("User denied") || 
          errorMessage.includes("user rejected") || 
          errorMessage.includes("denied transaction") ||
          errorMessage.includes("transaction was rejected")) {
        // Create a mock position for demo purposes
        const mockPosition = {
          id: `mock-${Date.now()}`,
          wallet_address: wallet.address,
          dex_name: exchange,
          coin_address: coin.address,
          coin_symbol: coin.symbol,
          side: side,
          amount: parseFloat(amount), // USDT amount
          entry_price: coin.price,
          take_profit: parseFloat(takeProfitPrice),
          status: 'open',
          created_at: new Date().toISOString()
        };
        
        // Call the onPositionCreated callback with the mock position
        onPositionCreated && onPositionCreated(mockPosition);
        
        // Clear form values just like a successful trade
        setAmount('10');
        setTakeProfitPercent(DEFAULT_TAKE_PROFIT_PERCENT.toString());
        
        // Set a user-friendly error message
        setError("Transaction was rejected by user, but a demo position was created.");
        
        // Return early to prevent showing the error message
        return;
      }

      setError(errorMessage);
      // Don't clear form values on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormContainer>
      <SectionTitle>Create Trade</SectionTitle>

      <CoinInfo>
        <CoinName>{coin.name} ({coin.symbol})</CoinName>
        <div>
          <TokenAmountLabel>Current Price</TokenAmountLabel>
          <CoinPrice>${currentPrice ? currentPrice.toFixed(8) : (coin.price ? coin.price.toFixed(8) : '0.00000000')}</CoinPrice>
          <TokenAmountLabel>You will trade for</TokenAmountLabel>
          <CoinPrice>{calculateTokenAmount()} {coin.symbol}</CoinPrice>
        </div>
      </CoinInfo>

      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Trading Timeframe</Label>
          <TimeframeOptions>
            {['1m', '3m', '5m', '30m', '1h'].map((tf) => (
              <TimeframeOption
                key={tf}
                selected={timeframe === tf}
                onClick={() => setTimeframe(tf)}
              >
                {tf}
              </TimeframeOption>
            ))}
          </TimeframeOptions>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="amount">Amount ({currentCurrency})</Label>
          <InputWrapper>
            <StyledInput
              type="text"
              id="amount"
              value={amount}
              onChange={(e) => {
                // Force comma to dot conversion for amount as well
                const value = e.target.value.replace(',', '.');
                // Only allow numbers, dots, and max one dot
                if (/^\d*\.?\d*$/.test(value)) {
                  setAmount(value);
                }
              }}
              placeholder={`Enter amount in ${currentCurrency}`}
              $hasIcon={false}
            />
          </InputWrapper>
        </FormGroup>

        <FormGroup>
          <Label>
            <FaChartLine />
            Take Profit Price (%)
          </Label>
          <InputWrapper>
            <StyledInput
              type="text"
              value={takeProfitPercent}
              onChange={(e) => handleTakeProfitChange(e.target.value)}
              placeholder="Enter take profit percentage (e.g., 1.5 for 1.5%)"
              $hasIcon={true}
              disabled={loading}
            />
            <InputSuffix>%</InputSuffix>
          </InputWrapper>
        </FormGroup>

     
        {/* Backtest button - only shown if backtest hasn't been run yet */}
        {!backtestRun && (
          <BacktestButton
            type="button"
            block
            onClick={runBacktestAnalysis}
            disabled={backtestLoading}
          >
            <FaChartArea size={20} style={{ marginRight: '8px' }} />
            {backtestLoading ? 'Running Backtest...' : 'Run Backtest'}
          </BacktestButton>
        )}
        
        {/* Backtest Results - only shown if backtest has been run */}
        {backtestResults && <BacktestResults results={backtestResults} loading={backtestLoading} />}

        {/* Only show wallet connection/trade buttons if backtest has been run */}
        {backtestRun && (
          <>
            {/* Trading disclaimer */}
            <InfoTooltip>
              💡 We will automatically deposit USDT to your trading wallet if needed. For BSC networks, we'll convert USDT to BNB internally as required.
            </InfoTooltip>
            
            {wallet ? (
              <Button
                type="submit"
                variant="success"
                block
                disabled={loading}
              >
                {loading ? 'Processing...' : `Trade ${coin.symbol}`}
              </Button>
            ) : (
              <MetamaskButton
                type="button"
                block
                onClick={onConnectWallet}
              >
                <FaFirefox size={20} style={{ color: '#FF9500' }} />
                Connect your crypto-wallet to trade!
              </MetamaskButton>
            )}
          </>
        )}

        {error && (
          <ErrorMessage>
            <FaExclamationTriangle />
            {error}
          </ErrorMessage>
        )}
      </form>
    </FormContainer>
  );
};

export default TradeForm;
